/**
 * Conversations deep link: /app/conversations?roomId=<conversation id> opens that exact thread.
 *
 * Used by the Action Center "View conversation" button (2026-09-16) and the dashboard Unread Messages
 * widget, which has linked here with ?roomId= for a long time while the page ignored it.
 *
 * Most threads are already in the room list (the listener loads the last 21 days / 100 rooms). A busy
 * office's thread can fall outside that window, so ConversationContainerV2 fetches the one doc and shows
 * it as a LOCAL room — the rooms listener keeps local rooms across snapshots and drops the local copy as
 * soon as the real one arrives. The shape below is the minimum vue-advanced-chat needs to render and
 * select a room (the same fields createRoom's local room carries); addLocalRoom fills timestamp + index.
 *
 * Import-free apart from firestoreTime: cypress/node-tests/conversations-room-deeplink.test.js runs it.
 */
import { coerceToMs } from './firestoreTime';

// Conversation ids are Firestore doc ids (legacy Twilio CH… SIDs, in-house conv_…). Never a path.
const ROOM_ID_RE = /^[A-Za-z0-9_-]{1,128}$/;

/** The requested room id, or null when absent / not a plausible doc id. */
export function readRoomIdQuery(query) {
  const raw = query && query.roomId;
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== 'string') return null;
  const id = value.trim();
  return ROOM_ID_RE.test(id) ? id : null;
}

function meaningful(v) {
  return typeof v === 'string' && v.trim() && v.trim().toLowerCase() !== 'unknown patient' ? v.trim() : null;
}

/**
 * A `conversations` doc → a local room, or null when it must not be opened here: missing, or another
 * practice's thread (a pasted link must never show a different site's patient).
 */
export function buildDeepLinkedRoom(roomId, data, { siteID } = {}) {
  if (!roomId || !data || typeof data !== 'object') return null;
  if (!siteID || data.siteID !== siteID) return null;

  const phone = meaningful(data.incomingPhoneNum) || meaningful(data.patientPhone);
  const name = meaningful(data.patientName) || meaningful(data.friendlyName) || phone || 'Conversation';
  const last = data.lastMessage && typeof data.lastMessage === 'object' ? data.lastMessage : {};
  const content = [last.content, last.body, last.text, last.message].find((v) => typeof v === 'string') || '';
  const ts = coerceToMs(last.timestampNum) || coerceToMs(data.dateUpdated) || coerceToMs(data.dateCreated) || Date.now();

  return {
    ...data,
    roomId,
    sid: roomId,
    roomName: name,
    friendlyName: data.friendlyName || name,
    channel: data.channel || 'sms',
    users: [
      { _id: 'site', username: 'Site' },
      { _id: phone || 'patient', username: name },
    ],
    unreadCount: 0,
    lastMessagePreview: content,
    lastMessage: {
      ...last,
      _id: typeof last._id === 'string' ? last._id : `last-${roomId}`,
      content,
      text: content,
      message: content,
      timestampNum: ts,
      date: ts,
    },
  };
}
